// Viewport — основной 3D-канвас Кубикон CAD (Fusion 360 style).
//
// Режимы (mode из store):
//   - 'view3d'        — обычный вид: тела, эскизы (видны), оси, сетка
//   - 'select_plane'  — показываем 3 плоскости XY/XZ/YZ для выбора
//   - 'sketch'        — камера развёрнута перпендикулярно к выбранной плоскости,
//                       идёт рисование. Сетка эскиза перпендикулярная плоскости.
//
// Фон: светло-серый градиент как в Fusion 360.

import React, { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import {
    OrbitControls, Html,
} from '@react-three/drei';
import * as THREE from 'three';

import useCadStore from '../store/useCadStore';
import { workplaneToPlaneRef } from '../workplaneGeometry';
import PlanePicker, { PLANES as PICK_PLANES, WorkplanesDisplay } from './PlanePicker';
import InfiniteGrid from './InfiniteGrid';
import FacePicker from './FacePicker';
import OrbitGizmo from './OrbitGizmo';
import TransformGizmo from './TransformGizmo';
import SketchOverlay from '../sketch/SketchOverlay';
import FaceActionMenu from './FaceActionMenu';
import ExtrudePreview from '../sketch/ExtrudePreview';
import FilletPreview from '../sketch/FilletPreview';
import { buildFaceProfile } from '../sketch/faceProfileBuilder';
import { addMeasurePick } from './measureController';


// ===== Тело (B-rep mesh) =====
//
// Клики по mesh:
//   - обычный клик     → выделить только это тело
//   - Shift + клик     → добавить к выделенной группе (или убрать если уже выделено)
//   - клик по пустому  → снять выделение (обработка в Canvas onPointerMissed)
//
// Клики по ребру (per-edge):
//   - обычный клик     → выделить ОДНО ребро (sel.kind='edge')
//   - Shift + клик     → toggle ребра в группе
//
// Подсветка: тело — оранжевый emissive; ребро — оранжевая толстая линия.
/*
 * Операции, работающие с ТЕЛОМ ЦЕЛИКОМ.
 *
 * 🔴 Список должен быть ОДИН. Раньше он был продублирован в двух местах —
 * в BodyMesh (клик по телу) и рядом с FacePicker (его отключение), — и они
 * разъехались: в обоих не хватало moveCopy, но починить надо было оба, иначе
 * FacePicker продолжал перехватывать клик своим невидимым мешем.
 *
 * Набор совпадает с операциями, которые в Properties.jsx ждут
 * `selection.kind === 'body'`.
 */
const BODY_PICK_KINDS = new Set([
    'combine', 'moveCopy', 'mirror', 'rectPattern', 'circPattern',
]);

const BodyMesh = ({ bodyId, mesh }) => {
    const setSelection = useCadStore((s) => s.setSelection);
    const selection    = useCadStore((s) => s.selection);
    const visibility   = useCadStore((s) => s.bodyVisibility);
    const openBodyContextMenu = useCadStore((s) => s.openBodyContextMenu);
    const mode = useCadStore((s) => s.mode);
    const pendingKind = useCadStore((s) => s.pendingFeature?.kind);
    const isBodySelected = selection.kind === 'body' && selection.ids.includes(bodyId);
    const visible = visibility.has(bodyId) ? visibility.get(bodyId) : true;
    // ПКМ (контекстное меню) на BodyMesh оставляем активным — Body
    // выделяется правым кликом или через дерево.
    const interactiveBodyContext = mode === 'view3d';
    // ЛКМ по телу включаем ТОЛЬКО когда активна операция, которой нужен
    // выбор тела целиком (combine/mirror/rectPattern). В остальное время
    // ЛКМ перехватывает FacePicker для выбора грани.
    const bodyPickingActive = mode === 'view3d' && BODY_PICK_KINDS.has(pendingKind);
    // Измерение — клик по поверхности тела даёт мировую точку (e.point).
    const measureActive = mode === 'view3d' && pendingKind === 'measure';

    const { geometry, edgeSegments } = useMemo(() => {
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.BufferAttribute(mesh.positions, 3));
        if (mesh.normals && mesh.normals.length > 0) {
            g.setAttribute('normal', new THREE.BufferAttribute(mesh.normals, 3));
        }
        g.setIndex(new THREE.BufferAttribute(mesh.indices, 1));
        if (!mesh.normals || mesh.normals.length === 0) {
            g.computeVertexNormals();
        }

        // Разбиваем общий edges на per-edge сегменты по edgeOffsets / edgeIds.
        // Старые меши без edgeOffsets fallback'ом дают один общий segment.
        const segs = [];
        if (mesh.edgeIds && mesh.edgeOffsets && mesh.edgeIds.length > 0) {
            for (let i = 0; i < mesh.edgeIds.length; i++) {
                const startVert = mesh.edgeOffsets[i];
                const endVert   = mesh.edgeOffsets[i + 1];
                const numVerts  = endVert - startVert;
                if (numVerts < 2) continue;
                const arr = new Float32Array(numVerts * 3);
                arr.set(mesh.edges.subarray(startVert * 3, endVert * 3));

                // Для onClick нужны парные сегменты; meshEdges уже даёт пары
                // (см. replicad addEdge) — каждые 2 точки это segment.
                const eg = new THREE.BufferGeometry();
                eg.setAttribute('position', new THREE.BufferAttribute(arr, 3));
                segs.push({ edgeId: mesh.edgeIds[i], geometry: eg });
            }
        } else {
            // Fallback: одно общее ребро без id (для старых mesh без edgeOffsets)
            const eg = new THREE.BufferGeometry();
            eg.setAttribute('position', new THREE.BufferAttribute(mesh.edges, 3));
            segs.push({ edgeId: -1, geometry: eg });
        }

        return { geometry: g, edgeSegments: segs };
    }, [mesh]);

    // ЛКМ по телу выделяет body — но только когда bodyPickingActive
    // (идёт операция combine/mirror/rectPattern). В обычном режиме
    // клик идёт на FacePicker и выделяет грань.
    const onMeshClick = (e) => {
        e.stopPropagation();
        const ne = e.nativeEvent;
        const multi = e.shiftKey || e.ctrlKey || e.metaKey
            || ne?.shiftKey || ne?.ctrlKey || ne?.metaKey;
        const cur = selection.kind === 'body' ? selection.ids : [];
        if (multi) {
            const next = cur.includes(bodyId)
                ? cur.filter((id) => id !== bodyId)
                : [...cur, bodyId];
            if (next.length === 0) setSelection(null, []);
            else                   setSelection('body', next);
        } else {
            setSelection('body', [bodyId]);
        }
    };

    // Клик по поверхности тела в режиме измерения. Если рядом (в пределах
    // порога) есть вершина триангуляции — снапим к ней (vertex-пик, как во
    // Fusion при наведении на угол). Иначе берём саму точку на поверхности.
    const onMeshClickMeasure = (e) => {
        e.stopPropagation();
        const p = [e.point.x, e.point.y, e.point.z];
        const pos = mesh.positions;
        // Ближайшая вершина меша к точке клика.
        let snap = p, bestD = Infinity;
        if (pos && pos.length >= 3) {
            for (let i = 0; i < pos.length; i += 3) {
                const d = Math.hypot(pos[i]-p[0], pos[i+1]-p[1], pos[i+2]-p[2]);
                if (d < bestD) { bestD = d; snap = [pos[i], pos[i+1], pos[i+2]]; }
            }
        }
        // Порог снапа ~ размер детали * малую долю; фикс 4мм разумен.
        const usePoint = bestD <= 4 ? snap : p;
        addMeasurePick({
            bodyId, kind: 'vertex', point: usePoint,
            render: { point: usePoint },
        });
    };

    const onMeshContextMenu = (e) => {
        e.stopPropagation();
        e.nativeEvent?.preventDefault?.();
        // Выделяем это тело + открываем меню в координатах клика
        setSelection('body', [bodyId]);
        const cx = e.nativeEvent?.clientX ?? 0;
        const cy = e.nativeEvent?.clientY ?? 0;
        openBodyContextMenu(bodyId, cx, cy);
    };

    // Невидимое тело — полностью исключаем из сцены (не рендерим, не кликается).
    if (!visible) return null;

    return (
        <group userData={{ bodyId }}>
            <mesh
                geometry={geometry}
                castShadow
                receiveShadow
                onClick={
                    measureActive ? onMeshClickMeasure
                    : bodyPickingActive ? onMeshClick
                    : undefined
                }
                onContextMenu={interactiveBodyContext ? onMeshContextMenu : undefined}
            >
                <meshStandardMaterial
                    color={isBodySelected ? '#ffb14a' : '#a8b8ce'}
                    emissive={isBodySelected ? '#ff7a1a' : '#000000'}
                    emissiveIntensity={isBodySelected ? 0.35 : 0}
                    metalness={0.05}
                    roughness={0.6}
                    flatShading={false}
                />
            </mesh>

            {edgeSegments.map(({ edgeId, geometry }, i) => (
                <EdgeLine
                    key={`${bodyId}-${edgeId}-${i}`}
                    bodyId={bodyId}
                    edgeId={edgeId}
                    geometry={geometry}
                    bodySelected={isBodySelected}
                />
            ))}
        </group>
    );
};


// Сумма длин сегментов полилинии [[x,y,z], ...]. Для прямого ребра —
// точная длина; для тесселированной дуги — приближённая.
function polylineLength(poly) {
    let len = 0;
    for (let i = 1; i < poly.length; i++) {
        const a = poly[i - 1], b = poly[i];
        len += Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
    }
    return len;
}

// Точка на середине полилинии (по накопленной длине) — совпадает с B-rep
// pointAt(0.5) для прямого/равномерного ребра, что позволяет worker'у
// сопоставить это ребро по mid+len.
function polylineMidpoint(poly) {
    if (!poly || poly.length === 0) return [0, 0, 0];
    if (poly.length === 1) return poly[0].slice();
    const total = polylineLength(poly);
    const half = total / 2;
    let acc = 0;
    for (let i = 1; i < poly.length; i++) {
        const a = poly[i - 1], b = poly[i];
        const seg = Math.hypot(b[0]-a[0], b[1]-a[1], b[2]-a[2]);
        if (acc + seg >= half) {
            const t = seg > 0 ? (half - acc) / seg : 0;
            return [a[0] + (b[0]-a[0])*t, a[1] + (b[1]-a[1])*t, a[2] + (b[2]-a[2])*t];
        }
        acc += seg;
    }
    return poly[poly.length - 1].slice();
}


// ===== Отдельное ребро как кликабельный <line> =====
const EdgeLine = ({ bodyId, edgeId, geometry, bodySelected }) => {
    const setSelection = useCadStore((s) => s.setSelection);
    const selection    = useCadStore((s) => s.selection);
    const refId = `${bodyId}/${edgeId}`;
    const [hovered, setHovered] = useState(false);
    const isEdgeSelected =
        selection.kind === 'edge' && selection.ids.includes(refId);

    const onOver = (e) => {
        if (edgeId < 0) return;
        e.stopPropagation();
        setHovered(true);
    };
    const onOut = (e) => {
        e.stopPropagation();
        setHovered(false);
    };

    const onClick = (e) => {
        e.stopPropagation();
        if (edgeId < 0) return; // fallback-ребро без id
        // Режим измерения — клик по ребру: пик 'edge' (mid+len для матчинга
        // в worker, polyline для отрисовки).
        const store = useCadStore.getState();
        const pf = store.pendingFeature;
        if (pf && pf.kind === 'measure') {
            const pos = geometry.getAttribute('position');
            const poly = [];
            for (let i = 0; i < pos.count; i++) {
                poly.push([pos.getX(i), pos.getY(i), pos.getZ(i)]);
            }
            const len = polylineLength(poly);
            const mid = polylineMidpoint(poly);
            addMeasurePick({
                bodyId, kind: 'edge', point: mid, mid, len,
                render: { point: mid, polyline: poly },
            });
            return;
        }
        // Ctrl или Shift — множественный выбор (toggle в наборе).
        const ne = e.nativeEvent;
        const multi = e.shiftKey || e.ctrlKey || e.metaKey
            || ne?.shiftKey || ne?.ctrlKey || ne?.metaKey;
        const cur = selection.kind === 'edge' ? selection.ids : [];
        if (multi) {
            const next = cur.includes(refId)
                ? cur.filter((id) => id !== refId)
                : [...cur, refId];
            if (next.length === 0) setSelection(null, []);
            else                   setSelection('edge', next);
        } else {
            setSelection('edge', [refId]);
        }
    };

    return (
        <line
            geometry={geometry}
            onClick={onClick}
            onPointerOver={onOver}
            onPointerOut={onOut}
            // userData помечаем как edge — FacePicker по этому маркеру
            // пропускает свой hover/onClick если в intersections есть edge.
            userData={{ kind: 'edge', bodyId, edgeId }}
        >
            <lineBasicMaterial
                color={
                    isEdgeSelected ? '#ff7a1a'
                    : hovered       ? '#ff9a3a'   // наведение — ярко-оранжевый
                    : bodySelected  ? '#cc5500'
                    : '#1a1a1c'
                }
                linewidth={(isEdgeSelected || hovered) ? 3 : (bodySelected ? 2 : 1.5)}
                depthTest={(isEdgeSelected || hovered) ? false : true}
                transparent={isEdgeSelected || hovered}
            />
        </line>
    );
};


// ===== Оси координат (XYZ-стрелки) =====
const ArrowAxis = ({ dir, color, length }) => {
    const direction = useMemo(() => new THREE.Vector3(...dir).normalize(), [dir]);
    const origin = useMemo(() => new THREE.Vector3(0, 0, 0), []);
    return (
        <arrowHelper
            args={[direction, origin, length, color, length * 0.12, length * 0.06]}
        />
    );
};

const OriginAxes = () => (
    <group>
        <ArrowAxis dir={[1, 0, 0]} color="#cc3344" length={30}/>
        <ArrowAxis dir={[0, 1, 0]} color="#0c8"    length={30}/>
        <ArrowAxis dir={[0, 0, 1]} color="#3a78ff" length={30}/>
    </group>
);


// ===== Оверлей инструмента «Измерение» =====
// Рисует маркеры кликнутых точек, линию измерения и подпись с числом.
// Читает pendingFeature kind='measure' напрямую из store.
const MeasureMarker = ({ pos, small }) => (
    <mesh position={pos}>
        <sphereGeometry args={[small ? 0.9 : 1.3, 16, 16]}/>
        <meshBasicMaterial color={small ? '#ff7a1a' : '#e8b73a'} depthTest={false}/>
    </mesh>
);

const MeasureLine = ({ a, b }) => {
    const geometry = useMemo(() => {
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.BufferAttribute(
            new Float32Array([...a, ...b]), 3));
        return g;
    }, [a, b]);
    return (
        <line geometry={geometry}>
            <lineBasicMaterial color="#e8b73a" linewidth={2} depthTest={false} transparent/>
        </line>
    );
};

/*
 * Наконечник размерной линии (конус), как в Fusion 360.
 *
 * at  — вершина стрелки (конец размерной линии);
 * dir — куда «смотрит» стрелка (единичный вектор ВДОЛЬ линии, внутрь размера);
 * size — длина конуса в мм модели.
 *
 * ConeGeometry смотрит вдоль +Y, поэтому доворачиваем кватернионом от [0,1,0]
 * к нужному направлению и сдвигаем на половину высоты — чтобы остриё попало
 * ровно в точку at.
 */
const MeasureArrow = ({ at, dir, size = 2 }) => {
    const { geometry, position, quaternion } = useMemo(() => {
        const h = Math.max(0.8, Math.min(4, size));
        const g = new THREE.ConeGeometry(h * 0.34, h, 12);
        const d = new THREE.Vector3(dir[0], dir[1], dir[2]).normalize();
        const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), d);
        // Центр конуса — на полвысоты от острия ВДОЛЬ направления взгляда.
        const p = new THREE.Vector3(at[0], at[1], at[2]).addScaledVector(d, h / 2);
        return { geometry: g, position: p, quaternion: q };
    }, [at, dir, size]);

    return (
        <mesh geometry={geometry} position={position} quaternion={quaternion}>
            <meshBasicMaterial color="#e8b73a" depthTest={false} transparent/>
        </mesh>
    );
};

const MeasureLabel = ({ pos, text }) => (
    <Html position={pos} center zIndexRange={[100, 0]}>
        <div style={{
            background: 'rgba(20,24,30,0.92)',
            border: '1px solid #e8b73a', borderRadius: 4,
            padding: '2px 7px', color: '#e8b73a',
            fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
            pointerEvents: 'none', userSelect: 'none',
        }}>
            {text}
        </div>
    </Html>
);

const MeasureOverlay = () => {
    const pf = useCadStore((s) => s.pendingFeature);
    if (!pf || pf.kind !== 'measure') return null;

    const picks = Array.isArray(pf.picks) ? pf.picks : [];
    const result = pf.result;

    // Подсветка выбранных сущностей: маркер точки + полилиния (для ребра).
    const highlights = picks.map((pk, i) => {
        const r = pk.render || {};
        return (
            <group key={i}>
                {r.point && <MeasureMarker pos={r.point}/>}
                {r.polyline && r.polyline.length >= 2 && (
                    <MeasurePolyline poly={r.polyline}/>
                )}
            </group>
        );
    });

    /*
     * Линия расстояния между двумя выборами.
     *
     * Для ПАРАЛЛЕЛЬНЫХ ПЛОСКОСТЕЙ показываем расстояние вдоль нормали
     * (planeP1/planeP2) — именно его человек и имеет в виду. Минимальное
     * расстояние там меряется по краям и выглядит непонятно, а при касании
     * углом вообще равно нулю.
     */
    let distLine = null;
    const usePlane = result && !result.error
        && result.planeDistance != null && result.planeP1 && result.planeP2;
    const dp1 = usePlane ? result.planeP1 : result?.p1;
    const dp2 = usePlane ? result.planeP2 : result?.p2;
    const dval = usePlane ? result.planeDistance : result?.distance;

    if (result && !result.error && result.count === 2
        && dp1 && dp2 && dval != null) {
        const mid = [(dp1[0]+dp2[0])/2, (dp1[1]+dp2[1])/2, (dp1[2]+dp2[2])/2];
        // Направление и стрелки — чтобы было видно, ОТКУДА КУДА мерим,
        // а не просто отрезок между точками (как в Fusion 360).
        const dv = [dp2[0]-dp1[0], dp2[1]-dp1[1], dp2[2]-dp1[2]];
        const dl = Math.hypot(dv[0], dv[1], dv[2]) || 1;
        const du = [dv[0]/dl, dv[1]/dl, dv[2]/dl];
        const asz = Math.max(1, Math.min(4, dl * 0.08));

        distLine = (
            <group>
                <MeasureMarker pos={dp1} small/>
                <MeasureMarker pos={dp2} small/>
                <MeasureLine a={dp1} b={dp2}/>
                {/* Стрелки смотрят внутрь размера, остриями в замеряемые точки */}
                <MeasureArrow at={dp1} dir={du} size={asz}/>
                <MeasureArrow at={dp2} dir={[-du[0], -du[1], -du[2]]} size={asz}/>
                <MeasureLabel pos={mid} text={`${dval.toFixed(2)} мм`}/>
            </group>
        );
    }

    /*
     * Размер ОДИНОЧНОЙ сущности — как в Fusion 360.
     *
     * Раньше линия с подписью появлялась только при ДВУХ выборах (расстояние
     * между ними). Выбрал одно ребро — в панели справа число есть, а в сцене
     * ничего: непонятно, что именно измерено.
     *
     * Теперь у прямого ребра рисуем полноценный размер: выносная линия сбоку
     * от ребра, засечки к его концам и подпись длины по центру.
     */
    let singleDim = null;
    if (result && !result.error && result.count === 1) {
        const ent = (result.entities && result.entities[0]) || null;
        if (ent && ent.kind === 'edge' && ent.start && ent.end
            && Number.isFinite(ent.length)) {
            const a = ent.start, b = ent.end;
            const dir = [b[0]-a[0], b[1]-a[1], b[2]-a[2]];
            const len = Math.hypot(dir[0], dir[1], dir[2]) || 1;
            const u = [dir[0]/len, dir[1]/len, dir[2]/len];
            // Перпендикуляр: векторное произведение с наименее сонаправленной осью.
            const ax = Math.abs(u[0]) < 0.9 ? [1,0,0] : [0,1,0];
            const p = [
                u[1]*ax[2] - u[2]*ax[1],
                u[2]*ax[0] - u[0]*ax[2],
                u[0]*ax[1] - u[1]*ax[0],
            ];
            const pl = Math.hypot(p[0], p[1], p[2]) || 1;
            // Отступ выносной линии — доля длины, чтобы размер не липнул к телу
            // и не улетал далеко на крупных деталях.
            const off = Math.max(3, Math.min(12, len * 0.18));
            const o = [p[0]/pl*off, p[1]/pl*off, p[2]/pl*off];
            const a2 = [a[0]+o[0], a[1]+o[1], a[2]+o[2]];
            const b2 = [b[0]+o[0], b[1]+o[1], b[2]+o[2]];
            const mid2 = [(a2[0]+b2[0])/2, (a2[1]+b2[1])/2, (a2[2]+b2[2])/2];
            singleDim = (
                <group>
                    {/* Выносные линии от концов ребра к размерной линии */}
                    <MeasureLine a={a} b={a2}/>
                    <MeasureLine a={b} b={b2}/>
                    {/* Сама размерная линия со стрелками и подпись по центру */}
                    <MeasureLine a={a2} b={b2}/>
                    <MeasureArrow at={a2} dir={u} size={len * 0.06}/>
                    <MeasureArrow at={b2} dir={[-u[0], -u[1], -u[2]]} size={len * 0.06}/>
                    <MeasureLabel pos={mid2} text={`${ent.length.toFixed(2)} мм`}/>
                </group>
            );
        }
    }

    return (
        <group>
            {highlights}
            {distLine}
            {singleDim}
        </group>
    );
};

// Полилиния ребра (подсветка выбранного ребра).
const MeasurePolyline = ({ poly }) => {
    const geometry = useMemo(() => {
        const g = new THREE.BufferGeometry();
        const flat = [];
        for (const p of poly) flat.push(p[0], p[1], p[2]);
        g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(flat), 3));
        return g;
    }, [poly]);
    return (
        <line geometry={geometry}>
            <lineBasicMaterial color="#e8b73a" linewidth={3} depthTest={false} transparent/>
        </line>
    );
};


// ===== Камера: плавно летит к выбранной плоскости =====
//
// Принимает desired position+target. Когда меняются — анимирует переход.
const CameraAnimator = ({ desiredPos, desiredTarget, controlsRef }) => {
    const { camera } = useThree();
    const animRef = useRef(null);

    useEffect(() => {
        if (!desiredPos || !controlsRef.current) return;
        // Запоминаем стартовые значения
        animRef.current = {
            startPos: camera.position.clone(),
            startTarget: controlsRef.current.target.clone(),
            endPos: new THREE.Vector3(...desiredPos),
            endTarget: new THREE.Vector3(...desiredTarget),
            startTime: performance.now(),
            duration: 500, // ms
        };
    }, [desiredPos, desiredTarget, camera, controlsRef]);

    useEffect(() => {
        let raf;
        const tick = () => {
            const a = animRef.current;
            if (a && controlsRef.current) {
                const t = Math.min(1, (performance.now() - a.startTime) / a.duration);
                const k = 1 - Math.pow(1 - t, 3); // ease-out cubic
                camera.position.lerpVectors(a.startPos, a.endPos, k);
                controlsRef.current.target.lerpVectors(a.startTarget, a.endTarget, k);
                controlsRef.current.update();
                if (t >= 1) animRef.current = null;
            }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [camera, controlsRef]);

    return null;
};


// === Bounding box всех видимых тел и эскизов ===
// Возвращает { center: [x,y,z], diag: number, min, max } или null если пусто.
// Используется чтобы ISO-камера после finish-sketch центрировалась на содержимое.
function computeContentBoundingBox(bodyMeshes, sketches) {
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    let any = false;

    for (const mesh of bodyMeshes.values()) {
        const p = mesh?.positions;
        if (!p || p.length === 0) continue;
        for (let i = 0; i < p.length; i += 3) {
            const x = p[i], y = p[i + 1], z = p[i + 2];
            if (x < minX) minX = x; if (x > maxX) maxX = x;
            if (y < minY) minY = y; if (y > maxY) maxY = y;
            if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
        }
        any = true;
    }

    // Эскизы: их точки в локальных координатах плоскости. Можно учитывать
    // приближённо — берём sketch.planeRef.center или offset как ориентир.
    for (const sk of (sketches || [])) {
        const pr = sk.planeRef;
        if (!pr) continue;
        let cx = 0, cy = 0, cz = 0;
        if (pr.center) {
            [cx, cy, cz] = pr.center;
        } else if (pr.plane === 'XY') cz = pr.offset || 0;
        else if (pr.plane === 'XZ') cy = -(pr.offset || 0);
        else if (pr.plane === 'YZ') cx = pr.offset || 0;
        if (cx < minX) minX = cx; if (cx > maxX) maxX = cx;
        if (cy < minY) minY = cy; if (cy > maxY) maxY = cy;
        if (cz < minZ) minZ = cz; if (cz > maxZ) maxZ = cz;
        any = true;
    }

    if (!any) return null;
    const dx = maxX - minX, dy = maxY - minY, dz = maxZ - minZ;
    const diag = Math.sqrt(dx * dx + dy * dy + dz * dz);
    return {
        center: [(minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2],
        diag,
        min: [minX, minY, minZ],
        max: [maxX, maxY, maxZ],
    };
}


// === Расчёт «полёта камеры» к плоскости ===
// planeId — 'XY' | 'XZ' | 'YZ'.
// offset  — смещение вдоль zDir плоскости (для sketch на грани тела).
// center  — необязательный world-центр конкретной грани. Если задан — камера
//           целится именно в него, а не в (0,0,offset). Это нужно когда
//           грань тела не пересекает ось координат (например, верх куба
//           [25..75]×[25..75]×{50}).
function cameraTargetForPlane(planeId, offset = 0, center = null, planeRef = null) {
    const D = 200; // дистанция от целевой точки до камеры

    // Если центр не задан — берём «проекцию начала координат на плоскость».
    let tx = 0, ty = 0, tz = 0;
    if (center) {
        tx = center[0]; ty = center[1]; tz = center[2];
    } else {
        if (planeId === 'XY') tz = offset;
        else if (planeId === 'XZ') ty = -offset;
        else if (planeId === 'YZ') tx = offset;
        else if (planeId === 'CUSTOM' && planeRef?.origin) {
            tx = planeRef.origin[0]; ty = planeRef.origin[1]; tz = planeRef.origin[2];
        }
    }

    if (planeId === 'CUSTOM' && planeRef?.normal) {
        // Камера смотрит на грань вдоль её нормали (с отступом D в направлении нормали).
        const n = planeRef.normal;
        // up подбираем перпендикулярный нормали — используем мировой Z если он
        // не параллелен n, иначе мировой Y.
        const upWorld = (Math.abs(n[2]) < 0.9) ? [0, 0, 1] : [0, 1, 0];
        return {
            pos: [tx + n[0] * D, ty + n[1] * D, tz + n[2] * D],
            target: [tx, ty, tz],
            up: upWorld,
        };
    }
    // Для именованных плоскостей (XY/XZ/YZ) камера должна стоять СО СТОРОНЫ
    // нормали грани, а не всегда с фиксированной стороны. Иначе для грани с
    // противоположной нормалью (задняя стенка куба) камера смотрит «сзади»,
    // и эскиз оказывается развёрнут на 180°. worldNormal приходит из
    // faceToPlaneRef ([0,0,±1] и т.п.); если его нет (эскиз на базовой
    // плоскости без грани) — берём дефолтную сторону.
    const wn = planeRef?.worldNormal || null;
    if (planeId === 'XY') {
        const sz = wn ? Math.sign(wn[2]) || 1 : 1;
        return { pos: [tx, ty, tz + D * sz], target: [tx, ty, tz], up: [0, 1, 0] };
    }
    if (planeId === 'XZ') {
        // Дефолт камеры XZ — со стороны -Y (ty - D). worldNormal.y задаёт сторону.
        const sy = wn ? Math.sign(wn[1]) || -1 : -1;
        return { pos: [tx, ty + D * sy, tz], target: [tx, ty, tz], up: [0, 0, 1] };
    }
    if (planeId === 'YZ') {
        const sx = wn ? Math.sign(wn[0]) || 1 : 1;
        return { pos: [tx + D * sx, ty, tz], target: [tx, ty, tz], up: [0, 0, 1] };
    }
    return { pos: [120, 120, 120], target: [0, 0, 0], up: [0, 0, 1] };
}


// === Мост от DOM-toolbar'а к 3D-камере ===
// SketchCameraToolbar (DOM) дёргает window.__kubikonCadCamera.* — методы
// этого компонента изменяют camera/controls внутри Canvas.
//
// pan: имитируем pan через прямой сдвиг target+camera в screen-space.
//      Используем те же оси что OrbitControls (right = camera.right, up = camera.up).
const CameraBridge = ({ desired }) => {
    const { camera, gl, controls } = useThree();

    useEffect(() => {
        const dom = gl.domElement;
        const tmp = new THREE.Vector3();
        const right = new THREE.Vector3();
        const up = new THREE.Vector3();
        const fwd = new THREE.Vector3();

        const dollyBy = (factor) => {
            // factor < 1 — приблизить, > 1 — отдалить
            if (!controls) return;
            tmp.copy(camera.position).sub(controls.target).multiplyScalar(factor);
            camera.position.copy(controls.target).add(tmp);
            controls.update();
        };

        const panByPixels = (dx, dy) => {
            if (!controls) return;
            const rect = dom.getBoundingClientRect();
            // Сколько мира на 1 пиксель: для perspective — приблизительно через FOV.
            const dist = camera.position.distanceTo(controls.target);
            const halfH = Math.tan((camera.fov || 35) * Math.PI / 360) * dist;
            const mmPerPx = (halfH * 2) / rect.height;

            camera.matrixWorld.extractBasis(right, up, fwd);
            const wx = -dx * mmPerPx;
            const wy = +dy * mmPerPx;
            const offset = right.clone().multiplyScalar(wx)
                .add(up.clone().multiplyScalar(wy));
            camera.position.add(offset);
            controls.target.add(offset);
            controls.update();
        };

        // Orbit (вращение камеры вокруг target).
        // dxPx — пиксели по горизонтали (yaw вокруг up), dyPx — по вертикали (pitch).
        // Используем сферические координаты как у OrbitControls.
        const orbitByPixels = (dxPx, dyPx) => {
            if (!controls) return;
            const rect = dom.getBoundingClientRect();
            const yawDelta   = -2 * Math.PI * dxPx / Math.max(1, rect.width);
            const pitchDelta = -2 * Math.PI * dyPx / Math.max(1, rect.height);
            // Вектор от target к camera в мире
            const offset = new THREE.Vector3().subVectors(camera.position, controls.target);
            // Разлагаем в сферических координатах относительно camera.up.
            const sph = new THREE.Spherical().setFromVector3(offset);
            sph.theta += yawDelta;
            sph.phi   += pitchDelta;
            // clamp phi чтобы камера не «перевернулась»
            const EPS = 0.01;
            sph.phi = Math.max(EPS, Math.min(Math.PI - EPS, sph.phi));
            offset.setFromSpherical(sph);
            camera.position.copy(controls.target).add(offset);
            camera.lookAt(controls.target);
            controls.update();
        };

        // Orbit вокруг конкретной мировой оси (для orbit-gizmo: 3 круга XY/XZ/YZ).
        // axisVec — единичный вектор оси (X/Y/Z), angleRad — угол поворота.
        const orbitAroundAxis = (axisVec, angleRad) => {
            if (!controls) return;
            const axis = new THREE.Vector3(axisVec[0], axisVec[1], axisVec[2]).normalize();
            const offset = new THREE.Vector3().subVectors(camera.position, controls.target);
            offset.applyAxisAngle(axis, angleRad);
            camera.position.copy(controls.target).add(offset);
            // up тоже крутим иначе картинка перекошена
            camera.up.applyAxisAngle(axis, angleRad).normalize();
            camera.lookAt(controls.target);
            controls.update();
        };

        // === Плавная анимация камеры ===
        // Все переходы вида (setView/rotateView/fit) идут через этот хелпер.
        // Интерполируем 3 величины: позицию камеры, target и up. Up идём через
        // нормализованный slerp (Quaternion.slerp на повороте, переводящем
        // baseUp → toUp), позиции — обычный lerp по дуге сферы вокруг target.
        let animToken = 0;
        const animateCamera = ({ toPos, toTarget, toUp, durMs = 380 }) => {
            if (!controls) return;
            animToken++;
            const myToken = animToken;
            // Стартовые значения — глубокая копия
            const fromPos    = camera.position.clone();
            const fromTarget = controls.target.clone();
            const fromUp     = camera.up.clone().normalize();
            const endTarget  = toTarget ? toTarget.clone() : fromTarget.clone();
            const endUp      = (toUp ? toUp.clone() : fromUp.clone()).normalize();
            const endPos     = toPos.clone();
            // Радиусы для slerp по дуге (чтобы не «прокатывало» близко к target)
            const r0 = fromPos.distanceTo(fromTarget);
            const r1 = endPos.distanceTo(endTarget);
            // Направления от target к camera в начале и конце (нормированные)
            const d0 = new THREE.Vector3().subVectors(fromPos, fromTarget).normalize();
            const d1 = new THREE.Vector3().subVectors(endPos, endTarget).normalize();
            // Кватернион между up'ами для slerp ориентации
            const qFrom = new THREE.Quaternion();
            const qTo   = new THREE.Quaternion().setFromUnitVectors(fromUp, endUp);
            // Отключаем OrbitControls на время анимации — иначе пользователь
            // мышью переколбасит target и анимация выйдет за рамки.
            const wasEnabled = controls.enabled;
            controls.enabled = false;
            const t0 = performance.now();
            const ease = (t) => t < 0.5
                ? 4 * t * t * t
                : 1 - Math.pow(-2 * t + 2, 3) / 2; // easeInOutCubic
            const step = () => {
                if (myToken !== animToken) return; // другая анимация перебила
                const tRaw = Math.min(1, (performance.now() - t0) / durMs);
                const t = ease(tRaw);
                // target — обычный lerp
                const tg = fromTarget.clone().lerp(endTarget, t);
                // direction — slerp через Quaternion.setFromUnitVectors
                const qDir = new THREE.Quaternion().setFromUnitVectors(d0, d1);
                const qPart = new THREE.Quaternion().slerp(qDir, t);
                const dirNow = d0.clone().applyQuaternion(qPart);
                const rNow = r0 + (r1 - r0) * t;
                camera.position.copy(tg).addScaledVector(dirNow, rNow);
                controls.target.copy(tg);
                // up — slerp кватернионов
                const qUp = qFrom.clone().slerp(qTo, t);
                camera.up.copy(fromUp.clone().applyQuaternion(qUp)).normalize();
                camera.lookAt(controls.target);
                controls.update();
                if (tRaw < 1) {
                    requestAnimationFrame(step);
                } else {
                    controls.enabled = wasEnabled;
                }
            };
            requestAnimationFrame(step);
        };

        const fit = () => {
            if (!desired) return;
            animateCamera({
                toPos:    new THREE.Vector3(...desired.pos),
                toTarget: new THREE.Vector3(...desired.target),
                toUp:     desired.up ? new THREE.Vector3(...desired.up) : null,
            });
        };

        // Установить камеру в стандартный вид (top/bottom/front/back/left/right/iso).
        const setView = (view) => {
            if (!controls) return;
            const target = controls.target.clone();
            const dist = camera.position.distanceTo(target) || 200;
            let pos, up;
            switch (view) {
                case 'top':    pos = [0, 0,  dist]; up = [0, 1, 0];  break;
                case 'bottom': pos = [0, 0, -dist]; up = [0, -1, 0]; break;
                case 'front':  pos = [0, -dist, 0]; up = [0, 0, 1];  break;
                case 'back':   pos = [0,  dist, 0]; up = [0, 0, 1];  break;
                case 'right':  pos = [ dist, 0, 0]; up = [0, 0, 1];  break;
                case 'left':   pos = [-dist, 0, 0]; up = [0, 0, 1];  break;
                case 'iso': {
                    const k = dist / Math.sqrt(3);
                    pos = [k, k, k]; up = [0, 0, 1];
                    break;
                }
                default: return;
            }
            animateCamera({
                toPos:    new THREE.Vector3(target.x + pos[0], target.y + pos[1], target.z + pos[2]),
                toTarget: target,
                toUp:     new THREE.Vector3(...up),
            });
        };

        // Поворот текущего вида на 90° (cw/ccw — вокруг up; up/down — вокруг right).
        const rotateView = (dir) => {
            if (!controls) return;
            const offset = new THREE.Vector3().subVectors(camera.position, controls.target);
            const angle = (dir === 'cw' || dir === 'down') ? -Math.PI / 2 : Math.PI / 2;
            let rotAxis;
            if (dir === 'cw' || dir === 'ccw') {
                rotAxis = new THREE.Vector3().copy(camera.up).normalize();
            } else {
                const r = new THREE.Vector3();
                camera.matrix.extractBasis(r, new THREE.Vector3(), new THREE.Vector3());
                rotAxis = r.normalize();
            }
            const newOffset = offset.clone().applyAxisAngle(rotAxis, angle);
            const newPos    = controls.target.clone().add(newOffset);
            const newUp     = camera.up.clone();
            if (dir === 'up' || dir === 'down') newUp.applyAxisAngle(rotAxis, angle).normalize();
            animateCamera({
                toPos:    newPos,
                toTarget: controls.target.clone(),
                toUp:     newUp,
                durMs:    280,
            });
        };

        window.__kubikonCadCamera = {
            zoomIn:  () => dollyBy(0.83),
            zoomOut: () => dollyBy(1.20),
            fit,
            panByPixels,
            orbitByPixels,
            orbitAroundAxis,
            setView,
            rotateView,
        };
        // Геттер матрицы камеры для ViewCube (CSS-3D синхронизация).
        // Возвращает column-major Float32Array(16).
        window.__kubikonCadCameraMatrix = () => camera.matrixWorld.elements;
        return () => {
            delete window.__kubikonCadCamera;
            delete window.__kubikonCadCameraMatrix;
        };
    }, [camera, gl, controls, desired]);

    return null;
};


// === Главный компонент ===
const Viewport = () => {
    const bodyMeshes = useCadStore((s) => s.bodyMeshes);
    const showGrid = useCadStore((s) => s.ui.gridVisible);
    const showOrigin = useCadStore((s) => s.ui.originVisible);
    const mode = useCadStore((s) => s.mode);
    const sketches = useCadStore((s) => s.doc.sketches);
    const activeSketchId = useCadStore((s) => s.activeSketchId);

    const enterSketchMode = useCadStore((s) => s.enterSketchMode);
    const addSketch = useCadStore((s) => s.addSketch);
    const addFeature = useCadStore((s) => s.addFeature);
    const clearSelection = useCadStore((s) => s.clearSelection);

    const controlsRef = useRef();

    // Режим «iso» — стандартное положение. Если есть тела/эскизы — целимся
    // в их центр и подбираем дистанцию по диагонали bbox; иначе fallback.
    const computeIsoView = useCallback(() => {
        const bb = computeContentBoundingBox(bodyMeshes, sketches);
        if (!bb) {
            return { pos: [120, 120, 120], target: [0, 0, 25], up: [0, 0, 1] };
        }
        const [cx, cy, cz] = bb.center;
        // Диагональ bbox * 1.6 даёт хороший fit для 35° fov.
        const D = Math.max(bb.diag * 1.6, 80);
        // Камера под 120° к плоскости XY (изометрия). Направление (1,1,1)/√3.
        const k = D / Math.sqrt(3);
        return {
            pos:    [cx + k, cy + k, cz + k],
            target: [cx, cy, cz],
            up:     [0, 0, 1],
        };
    }, [bodyMeshes, sketches]);

    // Когда меняется sketch'mode — определяем куда лететь камере
    const [desired, setDesired] = useState(null);

    // Запускаем анимацию камеры ТОЛЬКО когда меняется режим или активный
    // эскиз (вход в sketch / выход из него). НЕ зависим от bodyMeshes —
    // иначе каждое движение тела через гизмо триггерило бы CameraAnimator.
    useEffect(() => {
        if (mode === 'sketch') {
            const activeSketch = sketches.find((s) => s.id === activeSketchId);
            const planeId = activeSketch?.planeRef?.plane || 'XY';
            const offset  = activeSketch?.planeRef?.offset || 0;
            const center  = activeSketch?.planeRef?.center || null;
            // planeRef нужен для CUSTOM — он несёт origin/xDir/normal.
            setDesired(cameraTargetForPlane(planeId, offset, center, activeSketch?.planeRef));
        } else if (mode === 'view3d') {
            // По возврату в 3D — ISO с центром на содержимое сцены.
            // Снимок computeIsoView берём ОДИН раз при смене mode, дальше
            // движение тел/эскизов камеру уже не трогает.
            setDesired(computeIsoView());
        }
        // В режиме select_plane не двигаем камеру — пользователь сам выбирает
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, activeSketchId]);

    const bodies = useMemo(() => {
        return Array.from(bodyMeshes.entries()).map(([id, mesh]) => ({ id, mesh }));
    }, [bodyMeshes]);

    // === Выбор грани тела в обычном view3d ===
    // payload: { bodyId, faceId, planeRef, normal, points3d }
    // Записываем в selection и сохраняем данные грани отдельно — чтобы потом
    // handleNewExtrude мог их прочитать без повторного picker'а.
    // Если уже идёт активный extrude (pendingFeature) — сразу подставляем
    // грань в pendingFeature.faceProfile, чтобы preview/стрелка появились
    // не дожидаясь повторного нажатия «Выдавить».
    const setSelection = useCadStore((s) => s.setSelection);
    const setSelectedFaceData = useCadStore((s) => s.setSelectedFaceData);
    const onPickFaceForSelection = useCallback((payload) => {
        if (!payload || !payload.bodyId) return;
        const refId = `${payload.bodyId}/face_${payload.faceId}`;
        const faceData = {
            bodyId: payload.bodyId,
            faceId: payload.faceId,
            planeRef: payload.planeRef,
            normal: payload.normal && [payload.normal.x, payload.normal.y, payload.normal.z],
            points3d: payload.points3d,
        };
        // Ctrl/Shift — множественный выбор граней (toggle в наборе).
        const store0 = useCadStore.getState();
        const curSel = store0.selection;
        if (payload.multi && curSel.kind === 'face') {
            const cur = curSel.ids;
            const next = cur.includes(refId)
                ? cur.filter((id) => id !== refId)
                : [...cur, refId];
            if (next.length === 0) { setSelection(null, []); setSelectedFaceData(null); }
            else { setSelection('face', next); setSelectedFaceData(faceData); }
            return;
        }
        setSelection('face', [refId]);
        setSelectedFaceData(faceData);

        // Если активна операция «Выдавить» и юзер ещё не выбрал источник —
        // подставляем эту грань (live-binding selection → pendingFeature).
        const store = useCadStore.getState();
        const pf = store.pendingFeature;
        /*
         * ⚠️ ПРОФИЛЬ ЭСКИЗА ГЛАВНЕЕ ПОПУТНОГО ХИТА ПО ГРАНИ.
         *
         * Клик, которым юзер выбирает регион эскиза, попадает ещё и в грань
         * тела под курсором. Раньше грань дописывалась в активную операцию и
         * вытесняла эскиз (Properties смотрит faceProfile первым) — в ядро
         * уходил ВЕСЬ круг грани вместо выбранного фрагмента, и OCCT падал.
         */
        if (pf && pf.kind === 'extrude' && !pf.profileRefs?.length) {
            const profile = buildFaceProfile(faceData);
            if (profile) {
                store.updatePendingFeature({
                    faceProfile: profile,
                    faceRef: refId,
                    targetBodyId: faceData.bodyId,
                    // Если это первое поле и оно ещё пустое (первый клик после
                    // нажатия «Выдавить») — выставляем дефолт operation='add'.
                    // Если operation уже был выставлен ранее (юзер менял в форме)
                    // — оставляем как есть.
                    ...(pf.operation === 'new' && !pf.profileRefs?.length
                        ? { operation: 'add' }
                        : {}),
                });
            }
        }
    }, [setSelection, setSelectedFaceData]);

    // === Клик по плоскости в режиме select_plane ===
    // planeId — строка 'XY'|'XZ'|'YZ' (старый сигнал от PlanePicker)
    // ИЛИ объект { plane, offset, center? } от FacePicker (грань тела).
    // center — world-точка центра грани, нужен чтобы камера села ровно на неё.
    const onPickPlane = useCallback((picked) => {
        let planeRef;
        if (typeof picked === 'string') {
            planeRef = { kind: 'origin', plane: picked };
        } else if (picked.kind === 'workplane') {
            // Рабочая плоскость. Резолвим полный базис: offset → именованная
            // плоскость; angle/midplane/custom → CUSTOM с origin/xDir/normal
            // (тот же путь, что эскиз на наклонной грани тела).
            const wp = (useCadStore.getState().doc.workplanes || [])
                .find((w) => w.id === picked.workplaneId);
            const ref = wp ? workplaneToPlaneRef(wp) : null;
            planeRef = {
                kind: 'workplane',
                workplaneId: picked.workplaneId,
                ...(ref || { plane: picked.plane, offset: picked.offset || 0 }),
            };
        } else {
            planeRef = {
                kind: 'face',
                plane: picked.plane,
                offset: picked.offset || 0,
                // Тело-владелец грани — цель для extrude add/cut этого эскиза.
                ...(picked.bodyId ? { bodyId: picked.bodyId } : {}),
                ...(picked.center ? { center: picked.center } : {}),
                // worldNormal грани ([0,0,±1] и т.п.) — задаёт СТОРОНУ, с которой
                // камера смотрит на эскиз. Без него камера для задней грани
                // разворачивается на 180° (эскиз «вверх ногами»).
                ...(picked.worldNormal ? { worldNormal: picked.worldNormal } : {}),
                // CUSTOM-плоскость (наклонная грань) — сохраняем полный
                // базис для replicad.Plane: origin + xDir + normal.
                ...(picked.plane === 'CUSTOM' ? {
                    origin: picked.origin,
                    xDir: picked.xDir,
                    normal: picked.normal,
                } : {}),
                // Контур грани в 3D — для snap к углам при рисовании эскиза.
                ...(picked.points3d ? { points3d: picked.points3d } : {}),
            };
        }

        const sketchId = `sketch_${Date.now().toString(36)}`;
        const sketch = {
            id: sketchId,
            planeRef,
            primitives: [],
            constraints: [],
        };
        addSketch(sketch);
        // Добавляем feature-«создание эскиза» в timeline
        addFeature({
            id: `feat_${Date.now().toString(36)}`,
            kind: 'sketch',
            sketchId,
            planeRef,
            createdAt: Date.now(),
        });
        enterSketchMode(sketchId);
    }, [addSketch, addFeature, enterSketchMode]);

    /* Мост для контекстного меню грани (FaceActionMenu): «Эскиз на грани» и
       «Текст на грани» должны создавать эскиз ТЕМ ЖЕ кодом, что и обычный выбор
       плоскости — иначе логика (CUSTOM-базис, worldNormal, snap к углам)
       разъедется на два места. Такой же приём уже используется для drag'а
       гизмо и коммита рисования (window.__kubikonCad*). */
    useEffect(() => {
        window.__kubikonCadSketchOnFace = onPickPlane;
        return () => { delete window.__kubikonCadSketchOnFace; };
    }, [onPickPlane]);

    // В sketch-mode оставляем zoom (колесо) и pan (правая/средняя кнопка) —
    // отключаем только rotate, чтобы перпендикулярный вид на плоскость не сбился.
    // В select_plane / view3d — полный контроль.
    const isSketch = mode === 'sketch';

    // Если активна операция, которая работает с рёбрами (fillet/chamfer),
    // отключаем FacePicker — он перехватывает клики невидимым mesh'ем
    // поверх тела, и тонкие EdgeLine'ы становятся некликабельны.
    // То же для операций, выбирающих ТЕЛО целиком (combine/mirror/
    // rectPattern): FacePicker перехватил бы клик и выделил грань
    // вместо тела.
    const pendingKind = useCadStore((s) => s.pendingFeature?.kind);
    const needEdgePicking = pendingKind === 'fillet' || pendingKind === 'chamfer';
    // Тот же список: FacePicker надо выключить, иначе его невидимый меш
    // перехватит клик раньше, чем тот дойдёт до тела.
    const needBodyPicking = BODY_PICK_KINDS.has(pendingKind);
    // Измерение — FacePicker надо выключить, чтобы клики доходили до
    // BodyMesh (e.point) и EdgeLine.
    const measureActive = pendingKind === 'measure';

    return (
        <Canvas
            camera={{ position: [120, 120, 120], fov: 35, up: [0, 0, 1] }}
            // preserveDrawingBuffer=true — нужно чтобы canvas.toDataURL()
            // возвращал реальное содержимое (для thumbnail при сохранении
            // в облако). Иначе WebGL очищает буфер после кадра и
            // toDataURL даёт прозрачный PNG.
            /* alpha:true — фон даёт CSS-градиент под канвасом (см. style ниже
               и onCreated: setClearColor с нулевой альфой). При alpha:false
               прозрачный clear невозможен и градиент не виден. */
            gl={{ antialias: true, alpha: true, preserveDrawingBuffer: true }}
            shadows
            // Threshold для попаданий по линиям-рёбрам — увеличиваем чтобы
            // тонкие линии было удобно ловить курсором (без этого raycast
            // требует попадания пиксель-в-пиксель).
            raycaster={{ params: { Line: { threshold: 1.5 }, Points: { threshold: 1 } } }}
            // frameloop=always — для гарантии перерисовки при drag-рисовании
            // (иначе на быстром движении мыши preview может «застрять»).
            frameloop="always"
            // Градиентный светлый фон как в Fusion 360 (Sky → Ground):
            // сверху светлее/холоднее, снизу чуть темнее. CSS-градиент за
            // canvas + прозрачный clear, чтобы сетка растворялась именно в него.
            style={{ background: 'linear-gradient(to bottom, #e9ecef 0%, #cfd4d9 55%, #bcc2c8 100%)' }}
            /* ⚠️ Проп gl задан ВЫШЕ (antialias + preserveDrawingBuffer + alpha).
               Раньше он дублировался здесь, и второй объект затирал первый —
               вместе с preserveDrawingBuffer, из-за чего canvas.toDataURL()
               мог вернуть пустое превью при сохранении проекта в облако. */
            onCreated={({ gl, scene }) => {
                // Прозрачный clear — фон даёт CSS-градиент под canvas.
                gl.setClearColor(0x000000, 0);
                scene.background = null;
            }}
            // Клик по пустому месту в 3D-вьюпорте — снимаем выделение тел.
            // В sketch-mode не трогаем — там SketchOverlay сам управляет.
            onPointerMissed={(e) => {
                if (mode !== 'view3d') return;
                if (e?.button !== 0) return; // только ЛКМ
                clearSelection();
            }}
        >
            <CameraAnimator
                desiredPos={desired?.pos}
                desiredTarget={desired?.target}
                controlsRef={controlsRef}
            />

            {/* Освещение — мягкое CAD-style */}
            <ambientLight intensity={0.7}/>
            <directionalLight
                position={[100, 100, 200]}
                intensity={0.8}
                castShadow
                shadow-mapSize={[1024, 1024]}
            />
            <directionalLight position={[-100, -50, 100]} intensity={0.3}/>

            {/* Сетка пола — свой infinite-grid шейдер в стиле Fusion 360
                (major/minor два тона серого, чёткие линии через fwidth,
                fade в фон по alpha — без тёмного кольца по краям). */}
            {/* ⚠️ MeasureOverlay ПЕРВЫМ: внутри <Html> из drei, который
                вставляет DOM в обход React. Если перед ним появляются или
                исчезают соседи (сетка, оси, плоскости — все условные), React
                теряет позицию вставки и падает с insertBefore. На постоянной
                первой позиции этого случиться не может. */}
            <MeasureOverlay/>
            {/* Тоже содержит <Html> — держим рядом с MeasureOverlay, в начале
                списка, на постоянной позиции (см. комментарий выше). */}
            <FaceActionMenu/>

            {showGrid && <InfiniteGrid minor={10} major={50} distance={3000}/>}

            {/* Оси координат */}
            {showOrigin && <OriginAxes/>}

            {/* Рабочие плоскости — пассивная отрисовка в обычном
                view3d. В select_plane их рисует PlanePicker (интерактивно),
                поэтому здесь только когда НЕ выбор плоскости. */}
            {mode !== 'select_plane' && <WorkplanesDisplay/>}

            {/* Тела */}
            {bodies.map(({ id, mesh }) => (
                <BodyMesh key={id} bodyId={id} mesh={mesh}/>
            ))}

            {/* Эскизы (всегда видны — и в 3D, и в режиме скетча) */}
            <SketchOverlay/>

            {/* Preview активной операции (Extrude) с drag-стрелкой */}
            <ExtrudePreview/>

            {/* Preview скругления / фаски с drag-стрелкой возле выбранного ребра */}
            <FilletPreview/>

            {/* Выбор плоскости (создание эскиза) */}
            {mode === 'select_plane' && (
                <>
                    {/* Базовые плоскости origin-системы (XY/XZ/YZ) — маленькие, в центре */}
                    <PlanePicker onPick={onPickPlane}/>
                    {/* Hover-подсветка граней тел: наведи на грань → подсветится → клик → sketch на грани.
                        key общий с FacePicker'ом ниже: при переходе select_plane → sketch/view3d
                        React переиспользует ОДИН узел вместо «удалить + вставить». Раньше эти
                        две ветки давали размонтирование меша прямо в живой сцене three.js, и
                        вместе со сменой DOM-оверлея это роняло WebGL-контекст. */}
                    <FacePicker key="cad-face-picker" onPick={onPickPlane}/>
                </>
            )}

            {/* В обычном view3d — FacePicker для выделения грани (для extrude и др.).
                По клику записывает selection.kind='face' и selectedFaceData в store.
                Выключаем когда активна операция, требующая edge-выделения
                (fillet/chamfer) — иначе невидимый face-mesh перехватывает клики. */}
            {mode === 'view3d' && !needEdgePicking && !needBodyPicking
                && !measureActive && (
                <FacePicker key="cad-face-picker" onPick={onPickFaceForSelection} withLoop/>
            )}

            {/* Измерение: FacePicker в measure-режиме — клик по грани даёт
                пик 'face' (или 'vertex' при снапе к углу). Рёбра при этом
                кликаются приоритетно через EdgeLine. */}
            {mode === 'view3d' && measureActive && (
                <FacePicker measureMode/>
            )}

            {/* OrbitControls.
                В sketch-mode: rotate=false (плоскость не должна крутиться),
                zoom=true (колесо), pan=true (ПКМ/СКМ).
                В view3d/select_plane: всё включено. */}
            <OrbitControls
                ref={controlsRef}
                enabled
                enableRotate={!isSketch}
                enablePan
                enableZoom
                screenSpacePanning={isSketch}
                enableDamping
                dampingFactor={0.08}
                makeDefault
                target={[0, 0, 25]}
            />

            {/* Bridge для DOM-toolbar'а sketch-mode (Pan/Zoom/Fit).
                Кладёт API в window.__kubikonCadCamera */}
            <CameraBridge desired={desired}/>

            {/* Orbit-gizmo (3 круга вокруг target) — виден только когда
                cameraTool === 'orbit' */}
            <OrbitGizmo/>

            {/* TransformGizmo — 3 стрелки на превью-теле для перемещения.
                Виден только при создании/редактировании примитива (live). */}
            <TransformGizmo/>

            {/* ViewCube рендерится отдельным мини-Canvas'ом в DOM-overlay
                (см. <ViewCubeOverlay/> в KubikonCAD.jsx), не здесь. */}
        </Canvas>
    );
};

// Используется PICK_PLANES константой в внутренней логике
void PICK_PLANES;

export default Viewport;
